<template>
  <div class="sync-field-mappings" v-if="sortedFields.length > 0">
    <div v-for="field in sortedFields" :key="field.id" class="sync-field-mappings__field">
      <div class="sync-field-mappings__label">{{ field.label }}</div>

      <!-- Choice field -->
      <div v-if="field.fieldType === 'choice'">
        <FormSelect
          :options="getFieldOptions(field.id)"
          :value="values[field.valueExpression]"
          label-prop="label"
          value-prop="value"
          :placeholder="field.label"
          has-gray-background
          @input="onFieldChange(field.valueExpression, $event)"
        />
      </div>

      <!-- Lookup field -->
      <div v-if="field.fieldType === 'lookup'">
        <!-- Contact field with no account selected yet -->
        <div v-if="isContactWithoutAccount(field)" class="sync-field-mappings__no-account-message">
          {{ $t('opportunity-sync.contact-manipulations.no-account-selected') }}
        </div>
        <template v-else>
          <BaseMenu
            :items="lookupSearchResults[field.id] || []"
            :selection="getLookupSelection(field.valueExpression)"
            :attach="`.sync-field-mappings__lookup-${field.id}`"
            @select-item="onDropdownSelect(field, $event)"
            :searchQuery="lookupSearchQueries[field.id]"
            isUnclearable
            customActivation
            :ref="`lookupMenu_${field.id}`"
          >
            <template slot="activator">
              <BaseMenuSearchBar
                :class="`sync-field-mappings__lookup-${field.id}`"
                name="search"
                :iconName="entitySearchIcon"
                type="text"
                @search="onLookupSearch(field, $event)"
                hasGrayBackground
                @click.prevent
                :ref="`lookupSearchBar_${field.id}`"
              />
            </template>
            <template v-slot:default="{ item }">
              <div class="items">
                <div class="items__title">{{ getItemDisplayName(item) }}</div>
                <div class="items__subtitle">{{ item.email || item.id }}</div>
              </div>
            </template>
          </BaseMenu>

          <!-- Selected item chip (shown only when the selection came from the dropdown, not from suggestions) -->
          <div v-if="isCustomSelection[field.valueExpression]" class="sync-field-mappings__selected-lookup">
            {{ getItemDisplayName(selectedLookupItems[field.valueExpression]) }}
          </div>

          <!-- Loading state while accounts/contacts are being fetched -->
          <div v-if="loadingLookup[field.valueExpression]" class="sync-field-mappings__lookup-loading">
            <BaseCircularLoadingBar :size="14" :width="2" color="accent" />
          </div>

          <!-- Suggestion chips (always visible while suggestions exist; active chip = current selection) -->
          <template
            v-if="
              !loadingLookup[field.valueExpression] &&
              suggestedItems[field.valueExpression] &&
              suggestedItems[field.valueExpression].length > 0
            "
          >
            <div class="sync-field-mappings__suggestions-label">{{ $t('basic-words.suggestions') }}</div>
            <div class="sync-field-mappings__suggestions">
              <div
                v-for="(suggestion, i) in suggestedItems[field.valueExpression]"
                :key="i"
                class="sync-field-mappings__suggestion-chip"
                :class="{
                  active:
                    selectedLookupItems[field.valueExpression] &&
                    selectedLookupItems[field.valueExpression].id === suggestion.id,
                }"
                @click="onSuggestionSelect(field, suggestion)"
              >
                <div>{{ getItemDisplayName(suggestion) }}</div>
                <div v-if="suggestion.email" class="sync-field-mappings__suggestion-email">{{ suggestion.email }}</div>
              </div>
            </div>
          </template>

          <!-- Warning: selected contact has no email -->
          <div
            v-if="
              parseFieldConfig(field).lookupType === 'contact' &&
              selectedLookupItems[field.valueExpression] &&
              !selectedLookupItems[field.valueExpression].email
            "
            class="sync-field-mappings__no-email-warning"
          >
            {{ $t('quote-sync.account-has-no-email-warning') }}
          </div>

          <!-- Create new entity -->
          <template v-if="supportsCreate(parseFieldConfig(field))">
            <BaseCircularLoadingBar v-if="pendingCreate[field.valueExpression]" :size="14" :width="2" color="accent" />
            <template v-else-if="isCreating[field.valueExpression]">
              <div class="sync-field-mappings__create-form">
                <!-- Account form -->
                <template v-if="parseFieldConfig(field).lookupType === 'account'">
                  <FormInput
                    v-model="createForms[field.valueExpression].name"
                    :label="$t('page.admin-users.name')"
                    :name="`create-name-${field.id}`"
                    isRequired
                  />
                </template>
                <!-- Contact form -->
                <template v-if="parseFieldConfig(field).lookupType === 'contact'">
                  <FormInput
                    v-model="createForms[field.valueExpression].firstName"
                    :label="$t('page.admin-user-profile.first-name')"
                    :name="`create-first-${field.id}`"
                    isRequired
                  />
                  <FormInput
                    v-model="createForms[field.valueExpression].lastName"
                    :label="$t('page.admin-user-profile.last-name')"
                    :name="`create-last-${field.id}`"
                    isRequired
                  />
                  <div class="sync-field-mappings__create-email-row">
                    <div class="sync-field-mappings__create-email-input">
                      <FormInput
                        v-model="createForms[field.valueExpression].email"
                        :label="$t('page.opportunities.potential-bidder-dialog.contact-email')"
                        :name="`create-email-${field.id}`"
                        isRequired
                      />
                      <div v-if="hasContactEmailDuplicate(field)" class="sync-field-mappings__duplicate-email-error">
                        {{
                          $t('opportunity-sync.contact-manipulations.contact-exist', {
                            crmName: (syncProfile && syncProfile.name) || '',
                          })
                        }}
                      </div>
                    </div>
                    <div
                      v-if="tenantHasContactDirectoryAccess()"
                      class="sync-field-mappings__aloha-anchor"
                      :class="`sync-field-mappings__aloha-anchor-${field.id}`"
                    >
                      <BaseMenu
                        :items="contactDirectoryResults"
                        :selection="[]"
                        :attach="`.sync-field-mappings__aloha-anchor-${field.id}`"
                        @select-item="onAlohaContactSelect(field, $event)"
                        @custom-search="onAlohaCustomSearch(field, $event)"
                        :searchQuery="alohaSearchQueries[field.valueExpression]"
                        isUnclearable
                        searchAllowed
                        customSearchFunction
                        customActivation
                        :ref="`alohaMenu_${field.id}`"
                      >
                        <template slot="activator">
                          <BaseRoundButton
                            :tooltip-text="$t('opportunity-sync.contact-manipulations.search-aloha-directory')"
                            tooltipPlacement="top"
                            is-transparent
                            icon="account-search-outline"
                            is-neutral
                            @click.native.prevent="onAlohaTendersSearch(field)"
                          />
                        </template>
                        <template v-slot:default="{ item }">
                          <div class="items">
                            <div class="aloha-contact__name">{{ item.contactPoint.name }}</div>
                            <div class="aloha-contact__email">{{ item.contactPoint.email }}</div>
                            <div class="aloha-contact__phone">{{ item.contactPoint.telephone }}</div>
                          </div>
                        </template>
                      </BaseMenu>
                    </div>
                  </div>
                  <FormInput
                    v-model="createForms[field.valueExpression].phone"
                    :label="$t('page.opportunities.potential-bidder-dialog.contact-phone')"
                    :name="`create-phone-${field.id}`"
                  />
                </template>
                <div class="sync-field-mappings__create-form-actions">
                  <FormButton type="button" is-outlined @click.native="onCancelCreate(field)">
                    {{ $t('actions.cancel') }}
                  </FormButton>
                  <FormButton
                    type="button"
                    has-icon-left
                    :disabled="isCreateFormInvalid(field) || hasContactEmailDuplicate(field)"
                    @click.native="onSubmitCreate(field)"
                  >
                    <BaseIcon :name="entityIcon" class="sync-field-mappings__create-icon" />
                    {{ $t('actions.create') }}
                  </FormButton>
                </div>
              </div>
            </template>
            <BaseButton
              v-else
              isSmall
              isUnderline
              isTransparent
              isSquare
              class="sync-field-mappings__create-btn"
              @click.native="onStartCreate(field)"
            >
              <BaseIcon :name="entityIcon" class="sync-field-mappings__create-icon" />
              {{ $t(getCreateLabel(parseFieldConfig(field))) }}
            </BaseButton>
          </template>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import BaseButton from '@/components/base/BaseButton';
import BaseCircularLoadingBar from '@/components/base/BaseCircularLoadingBar';
import BaseIcon from '@/components/base/BaseIcon';
import BaseMenu from '@/components/base/BaseMenu';
import BaseMenuSearchBar from '@/components/pdf-viewer/BaseMenuSearchBar';
import BaseRoundButton from '@/components/base/BaseRoundButton';
import FormButton from '@/components/form/FormButton';
import FormInput from '@/components/form/FormInput';
import FormSelect from '@/components/form/FormSelect';
import DebounceMixin from '@/mixins/debounce-mixin';

import { getEntityIconPath } from '@/util/entity-crud-util';
import { tenantHasContactDirectoryAccess } from '@/util/module-access-util';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'SyncFieldMappings',
  mixins: [DebounceMixin],
  components: {
    BaseButton,
    BaseCircularLoadingBar,
    BaseIcon,
    BaseMenu,
    BaseMenuSearchBar,
    BaseRoundButton,
    FormButton,
    FormInput,
    FormSelect,
  },
  props: {
    fieldMappings: {
      type: Array,
      required: true,
      default: () => [],
    },
    values: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    syncProfileId: {
      type: String,
      required: true,
    },
    isCreate: {
      type: Boolean,
      default: true,
    },
    defaultQueries: {
      type: Object,
      default: () => ({}),
    },
    syncProfile: {
      type: Object,
      default: null,
    },
    createDefaults: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      localValues: {},
      lookupSearchQueries: {},
      lookupSearchResults: {},
      selectedLookupItems: {},
      suggestedItems: {},
      isCustomSelection: {},
      isCreating: {},
      createForms: {},
      pendingCreate: {},
      alohaSearchQueries: {},
      loadingLookup: {},
    };
  },
  computed: {
    ...mapGetters('syncFieldMappings', ['fieldOptions']),
    ...mapGetters('contactDirectory', { contactDirectoryResults: 'contactResults' }),
    entityIcon() {
      return getEntityIconPath(this.syncProfile);
    },
    entitySearchIcon() {
      return getEntityIconPath(this.syncProfile) + '-search';
    },
    sortedFields() {
      return this.fieldMappings
        .filter((f) => (this.isCreate ? f.syncMode !== 'UPDATE_ONLY' : f.syncMode !== 'CREATE_ONLY'))
        .sort((a, b) => a.displayOrder - b.displayOrder);
    },
  },
  watch: {
    values: {
      immediate: true,
      handler(v) {
        this.localValues = { ...v };
      },
    },
    fieldMappings: {
      immediate: true,
      handler(newFields) {
        this.fetchOptionsForChoiceFields(newFields);
        if (Object.keys(this.defaultQueries).length > 0) {
          this.prefetchDefaultQueries(newFields);
        }
      },
    },
    defaultQueries: {
      deep: true,
      handler() {
        if (this.fieldMappings.length > 0 && Object.keys(this.defaultQueries).length > 0) {
          this.prefetchDefaultQueries(this.fieldMappings);
        }
      },
    },
  },
  methods: {
    ...mapActions('syncFieldMappings', ['getFieldMappingOptions']),
    ...mapActions('opportunitySyncProfileAccounts', ['addAccount', 'searchUser', 'searchAccounts']),
    ...mapActions('opportunitySyncProfileContacts', ['addContact', 'getAllContacts', 'searchContacts']),
    ...mapActions('contactDirectory', ['searchContactDirectory']),
    tenantHasContactDirectoryAccess,
    fetchOptionsForChoiceFields(fields) {
      fields
        .filter((f) => f.fieldType === 'choice')
        .forEach((field) => {
          if (!this.fieldOptions[field.id]) {
            this.getFieldMappingOptions({ syncProfileId: this.syncProfileId, fieldId: field.id });
          }
        });
    },
    async prefetchDefaultQueries(fields) {
      const sortedFields = [...fields]
        .filter((f) => f.fieldType === 'lookup' && this.defaultQueries[f.valueExpression])
        .sort((a, b) => a.displayOrder - b.displayOrder);

      // Track IDs resolved so far so dependent fields (e.g. contact → account) can
      // reference them before the parent has processed the @input event.
      const resolvedValues = { ...this.localValues };

      for (const field of sortedFields) {
        this.$set(this.loadingLookup, field.valueExpression, true);
        try {
          const config = typeof field.fieldConfig === 'string' ? JSON.parse(field.fieldConfig) : field.fieldConfig;
          if (config && config.lookupType === 'contact') {
            await this.prefetchContactField(field, config, resolvedValues);
          } else {
            const query = this.defaultQueries[field.valueExpression];
            const searchPromise = this.getLookupSearchPromise(config, query, resolvedValues);
            if (searchPromise) {
              const results = await searchPromise;
              this.$set(this.suggestedItems, field.valueExpression, results || []);
              if (results && results.length > 0) {
                this.onSuggestionSelect(field, results[0]);
                resolvedValues[field.valueExpression] = results[0].id;
              }
            }
          }
        } finally {
          this.$set(this.loadingLookup, field.valueExpression, false);
        }
      }
    },
    parseFieldConfig(field) {
      if (!field.fieldConfig) return {};
      return typeof field.fieldConfig === 'string' ? JSON.parse(field.fieldConfig) : field.fieldConfig;
    },
    isContactWithoutAccount(field) {
      const config = this.parseFieldConfig(field);
      if (config.lookupType !== 'contact' || !config.accountExpression) return false;
      return !this.localValues[config.accountExpression];
    },
    supportsCreate(config) {
      if (!config || config.allowCreate === false) return false;
      return config.lookupType === 'account' || config.lookupType === 'contact';
    },
    getCreateLabel(config) {
      if (config.lookupType === 'contact') return 'opportunity-sync.contact-manipulations.add-contact';
      return 'opportunity-sync.account-manipulations.add-account';
    },
    onStartCreate(field) {
      // Clear the current selection — the user is creating a new entity, not picking an existing one
      const { [field.valueExpression]: _removed, ...rest } = this.selectedLookupItems;
      this.selectedLookupItems = rest;
      this.$set(this.isCustomSelection, field.valueExpression, false);
      this.localValues = { ...this.localValues, [field.valueExpression]: null };
      this.$emit('input', this.localValues);

      const config = this.parseFieldConfig(field);
      const defaults = this.createDefaults[field.valueExpression] || {};
      const form =
        config.lookupType === 'contact'
          ? {
              firstName: defaults.firstName || '',
              lastName: defaults.lastName || '',
              email: defaults.email || '',
              phone: defaults.phone || '',
            }
          : { name: defaults.name || '' };
      this.$set(this.createForms, field.valueExpression, form);
      this.$set(this.isCreating, field.valueExpression, true);
    },
    onCancelCreate(field) {
      this.$set(this.isCreating, field.valueExpression, false);
    },
    hasContactEmailDuplicate(field) {
      const form = this.createForms[field.valueExpression];
      if (!form?.email) return false;
      const existing = this.suggestedItems[field.valueExpression] || [];
      return existing.some((c) => c.email && c.email.toLowerCase() === form.email.toLowerCase());
    },
    isCreateFormInvalid(field) {
      const form = this.createForms[field.valueExpression];
      if (!form) return true;
      const config = this.parseFieldConfig(field);
      if (config.lookupType === 'contact') {
        return !form.firstName || !form.lastName || !form.email;
      }
      return !form.name;
    },
    async onSubmitCreate(field) {
      const config = this.parseFieldConfig(field);
      const form = this.createForms[field.valueExpression];

      // Duplicate email check against already-loaded contacts for this account
      if (config.lookupType === 'contact' && this.hasContactEmailDuplicate(field)) {
        return;
      }

      this.$set(this.pendingCreate, field.valueExpression, true);
      try {
        let created;
        if (config.lookupType === 'account') {
          created = await this.addAccount({ syncProfileId: this.syncProfileId, name: form.name });
        } else if (config.lookupType === 'contact') {
          const accountId = config.accountExpression ? this.localValues[config.accountExpression] ?? null : null;
          created = await this.addContact({
            syncProfileId: this.syncProfileId,
            externalAccountId: accountId,
            firstName: form.firstName,
            lastName: form.lastName,
            email: form.email,
            telephone: form.phone,
          });
        }
        if (created) {
          // Add to suggestions list, deduplicating in case a background re-fetch already returned it
          const current = this.suggestedItems[field.valueExpression] || [];
          const deduped = current.filter((c) => c.id !== created.id);
          this.$set(this.suggestedItems, field.valueExpression, [...deduped, created]);
          this.onSuggestionSelect(field, created);
          this.$set(this.isCreating, field.valueExpression, false);
          if (config.lookupType === 'contact') {
            this.$toast.success(this.$t('opportunity-sync.contact-manipulations.add-contact-success'));
          } else if (config.lookupType === 'account') {
            this.$toast.success(this.$t('opportunity-sync.account-manipulations.add-account-success'));
          }
        }
      } catch {
        if (config.lookupType === 'contact') {
          this.$toast.error(this.$t('opportunity-sync.contact-manipulations.add-contact-error'));
        } else if (config.lookupType === 'account') {
          this.$toast.error(this.$t('opportunity-sync.account-manipulations.add-account-error'));
        }
      } finally {
        this.$set(this.pendingCreate, field.valueExpression, false);
      }
    },
    getItemDisplayName(item) {
      if (!item) return '';
      if (item.fullName) return item.fullName;
      if (item.firstName || item.lastName) return `${item.firstName || ''} ${item.lastName || ''}`.trim();
      return item.name || item.id || '';
    },
    getFieldOptions(fieldId) {
      return this.fieldOptions[fieldId] || [];
    },
    getLookupSelection(valueExpression) {
      const item = this.selectedLookupItems[valueExpression];
      return item ? [item] : [];
    },
    onFieldChange(valueExpression, value) {
      this.localValues = { ...this.localValues, [valueExpression]: value };
      this.$emit('input', this.localValues);
    },
    onSuggestionSelect(field, item) {
      this.$set(this.isCustomSelection, field.valueExpression, false);
      this.$set(this.isCreating, field.valueExpression, false);
      this.applyLookupSelection(field, item);
    },
    onDropdownSelect(field, item) {
      this.$set(this.isCustomSelection, field.valueExpression, true);
      this.applyLookupSelection(field, item);
    },
    applyLookupSelection(field, item) {
      this.selectedLookupItems = { ...this.selectedLookupItems, [field.valueExpression]: item };
      this.localValues = { ...this.localValues, [field.valueExpression]: item.id };
      this.$emit('input', this.localValues);
      this.refreshDependentFields(field);
    },
    refreshDependentFields(changedField) {
      const dependentFields = this.fieldMappings.filter((f) => {
        if (f.fieldType !== 'lookup' || !f.fieldConfig) return false;
        const config = typeof f.fieldConfig === 'string' ? JSON.parse(f.fieldConfig) : f.fieldConfig;
        return config.accountExpression === changedField.valueExpression;
      });

      for (const depField of dependentFields) {
        // Clear the dependent field's current selection and suggestions
        const { [depField.valueExpression]: _removed, ...rest } = this.selectedLookupItems;
        this.selectedLookupItems = rest;
        this.$set(this.isCustomSelection, depField.valueExpression, false);
        this.$set(this.suggestedItems, depField.valueExpression, []);
        this.localValues = { ...this.localValues, [depField.valueExpression]: null };
        this.$emit('input', this.localValues);

        const config =
          typeof depField.fieldConfig === 'string' ? JSON.parse(depField.fieldConfig) : depField.fieldConfig;

        if (config && config.lookupType === 'contact') {
          this.prefetchContactField(depField, config, { ...this.localValues });
        } else {
          const query = this.defaultQueries[depField.valueExpression];
          if (!query) continue;
          const searchPromise = this.getLookupSearchPromise(config, query, this.localValues);
          if (searchPromise) {
            this.$set(this.loadingLookup, depField.valueExpression, true);
            searchPromise
              .then((results) => {
                this.$set(this.suggestedItems, depField.valueExpression, results || []);
                if (results && results.length > 0) {
                  this.onSuggestionSelect(depField, results[0]);
                }
              })
              .finally(() => {
                this.$set(this.loadingLookup, depField.valueExpression, false);
              });
          }
        }
      }
    },
    async prefetchContactField(field, config, resolvedValues) {
      const accountId = config.accountExpression ? resolvedValues[config.accountExpression] ?? null : null;
      if (!accountId) return;

      this.$set(this.loadingLookup, field.valueExpression, true);
      try {
        // 1. Fetch all contacts for the account to show as suggestion chips
        const allContacts = await this.getAllContacts({ syncProfileId: this.syncProfileId, accountId });
        const uniqueContacts = (allContacts || []).filter((c, i, arr) => arr.findIndex((x) => x.id === c.id) === i);
        this.$set(this.suggestedItems, field.valueExpression, uniqueContacts);

        if (!allContacts || allContacts.length === 0) return;

        // 2. Search with the default query to find and auto-select the best match
        const query = this.defaultQueries[field.valueExpression];
        let bestMatch = allContacts[0];
        if (query) {
          const searchResults = await this.searchContacts({
            syncProfileId: this.syncProfileId,
            searchQuery: query,
            accountId,
          });
          if (searchResults && searchResults.length > 0) {
            bestMatch = searchResults[0];
          }
        }
        this.onSuggestionSelect(field, bestMatch);
        resolvedValues[field.valueExpression] = bestMatch.id;
      } finally {
        this.$set(this.loadingLookup, field.valueExpression, false);
      }
    },
    getLookupSearchPromise(config, searchQuery, resolvedValues = null) {
      if (!config || !config.lookupType) return null;

      const params = { syncProfileId: this.syncProfileId, searchQuery };
      const values = resolvedValues ?? this.localValues;

      switch (config.lookupType) {
        case 'user':
          return this.searchUser(params);
        case 'account':
          return this.searchAccounts(params);
        case 'contact': {
          const accountId = config.accountExpression ? values[config.accountExpression] ?? null : null;
          return this.searchContacts({ ...params, accountId });
        }
        default:
          console.warn(`Unsupported lookupType: ${config.lookupType}`);
          return null;
      }
    },
    onLookupSearch(field, event) {
      this.debounce(() => {
        this.$set(this.lookupSearchQueries, field.id, event);
        if (!event) {
          this.$set(this.lookupSearchResults, field.id, []);
          return;
        }

        const config = typeof field.fieldConfig === 'string' ? JSON.parse(field.fieldConfig) : field.fieldConfig;
        const searchPromise = this.getLookupSearchPromise(config, event);
        if (searchPromise) {
          searchPromise.then((results) => {
            this.$set(this.lookupSearchResults, field.id, results || []);
            const menuRef = this.$refs[`lookupMenu_${field.id}`];
            if (menuRef && menuRef[0]) {
              menuRef[0].openMenu();
            }
          });
        }
      }, 700);
    },
    onAlohaTendersSearch(field) {
      const form = this.createForms[field.valueExpression] || {};
      // Prefer whatever the user typed in the lookup search bar; fall back to the create form names
      const lookupQuery = this.lookupSearchQueries[field.id] || '';
      const formQuery = [form.firstName, form.lastName].filter(Boolean).join(' ');
      const query = lookupQuery || formQuery;
      this.$set(this.alohaSearchQueries, field.valueExpression, query);
      const menuRef = this.$refs[`alohaMenu_${field.id}`];
      if (menuRef && menuRef[0]) menuRef[0].openMenu();
      if (query) {
        this.searchContactDirectory({ keywords: [query], entityType: 'CONTACT' });
      }
    },
    onAlohaCustomSearch(field, query) {
      this.debounce(() => {
        if (!query) return;
        this.$set(this.alohaSearchQueries, field.valueExpression, query);
        this.searchContactDirectory({ keywords: [query], entityType: 'CONTACT' });
      }, 700);
    },
    onAlohaContactSelect(field, item) {
      if (!item || !item.contactPoint) return;
      const form = this.createForms[field.valueExpression];
      if (form) {
        form.email = item.contactPoint.email || form.email;
      }
    },
  },
};
</script>

<style lang="scss">
.sync-field-mappings {
  &__field {
    margin-bottom: 20px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__label {
    font-size: 16px;
    font-weight: 700;
    margin-bottom: 8px;
  }

  &__selected-lookup {
    background: $color-accent-100;
    border-radius: 5px;
    font-size: 14px;
    padding: 4px 8px;
    margin-top: 4px;
    color: $color-accent;
    outline: 1px solid $color-accent-200;
  }

  &__lookup-loading {
    display: flex;
    align-items: center;
    padding: 8px 0;
  }

  &__suggestions-label {
    margin: 10px 0 6px;
    font-size: 13px;
    color: $color-normal-pale;
  }

  &__suggestions {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 4px;
    margin-bottom: 10px;
  }

  &__suggestion-chip {
    background: $color-neutral-pale;
    border-radius: 5px;
    font-size: 14px;
    padding: 4px 8px;
    cursor: pointer;
    transition: all 200ms ease;

    &:hover {
      background: $color-hover;
      color: $color-normal;
    }

    &.active {
      color: $color-accent;
      outline: 1px solid $color-accent-300;
      background: $color-accent-100;
    }
  }

  &__suggestion-email {
    font-size: 12px;
    color: $color-normal-pale;
    margin-top: 1px;
  }

  &__create-btn {
    margin-top: 10px;
  }

  &__create-icon {
    width: 16px;
    height: 16px;
    margin-right: 4px;
  }

  &__create-form {
    background: $color-accent-50;
    border-radius: 5px;
    padding: 12px;
    margin-top: 12px;

    .form-input {
      padding-bottom: 12px;
    }

    .form-label {
      font-size: 14px;
    }
  }

  &__create-form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 4px;
  }

  &__no-account-message {
    font-size: 13px;
    color: $color-normal-pale;
    font-style: italic;
    padding: 4px 0;
  }

  &__no-email-warning {
    font-size: 13px;
    color: $color-error;
    margin-top: 6px;
  }

  &__create-email-row {
    display: flex;
    align-items: flex-start;
    gap: 8px;
  }

  &__create-email-input {
    flex: 1;

    .form-input {
      padding-bottom: 0;
    }
  }

  &__duplicate-email-error {
    font-size: 12px;
    color: $color-error;
    margin-top: 4px;
    padding-bottom: 12px;
  }

  &__aloha-anchor {
    position: relative;
    display: inline-flex;
    align-items: center;
    align-self: flex-start;
    margin-top: 20px; // visually align with the input field
    flex-shrink: 0;

    // Force the dropdown above-left of the button, overriding Vuetify's inline top/left.
    // !important is required to beat inline style="top:…; left:…" that Vuetify injects.
    .base-menu {
      margin-top: 0 !important;
      top: auto !important;
      left: auto !important;
      right: 0 !important;
      bottom: calc(100% + 8px) !important;
    }
  }
}

.aloha-contact {
  &__name {
    font-size: 14px;
    font-weight: 600;
  }

  &__email,
  &__phone {
    font-size: 12px;
    color: $color-normal-pale;
  }
}
</style>
